import { memberDisplayName } from '@noconflict/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { NotFoundPage } from '@/widgets/not-found';
import { useOrgGrants, useOrgMembers } from '../../entities/member';
import { useOrgContext } from '../../entities/org';
import { InviteMemberDialog } from '../../features/member/invite-member';
import { GrantsEditor } from '../../features/member/manage-grants';
import { PendingInvitations } from '../../features/member/pending-invitations';
import { ArchiveWorkspaceButton } from '../../features/workspace/archive-workspace';
import { RenameWorkspaceForm } from '../../features/workspace/rename-workspace';
import { Button } from '../../shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/card';
import { ErrorState } from '../../shared/ui/error-state';
import { Notice } from '../../shared/ui/notice';
import { PageLayout } from '../../shared/ui/page-layout';
import { Skeleton } from '../../shared/ui/skeleton';
import { SettingsBreadcrumb } from './settings-page';

/**
 * `/o/:orgSlug/settings/workspaces/:workspaceSlug` (`workspace.manage`): rename, access, archive. A workspace manager
 * gets the same grants editor as the Members tab, scoped to this one workspace. Without the permission this
 * is the standard not-found page.
 */
export function WorkspaceSettingsPage() {
  const { t } = useTranslation(['workspace', 'member', 'org']);
  const { workspaceSlug = '' } = useParams();
  const { org, workspaceBySlug, canIn } = useOrgContext();
  const workspace = workspaceBySlug(workspaceSlug);
  const allowed = workspace ? canIn(workspace.id, 'workspace.manage') : false;
  useDocumentTitle(t('settings.title'));
  const canGrant = workspace ? canIn(workspace.id, 'grants.manage') : false;
  const members = useOrgMembers(org.id, allowed && canGrant);
  const grants = useOrgGrants(org.id, allowed && canGrant);
  const [inviting, setInviting] = useState(false);

  if (!workspace || !allowed) return <NotFoundPage />;
  const archived = workspace.archived_at !== null;
  // Owners and admins already hold every workspace permission: a grant would change nothing for them.
  const grantable = members.data?.filter((m) => m.role !== 'owner' && m.role !== 'admin') ?? [];

  return (
    <PageLayout title={workspace.name} description={t('settings.title')}>
      <SettingsBreadcrumb
        trail={[
          { label: t('org:settings.tabs.workspaces'), to: `/o/${org.slug}/settings/workspaces` },
          { label: workspace.name },
        ]}
      />

      {archived ? <Notice tone="warning">{t('archivedBanner')}</Notice> : null}

      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('settings.detailsTitle')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RenameWorkspaceForm workspace={workspace} />
        </CardContent>
      </Card>

      {canGrant ? (
        <Card>
          <CardHeader className="flex flex-wrap items-start justify-between gap-3">
            <div className="grid gap-1.5">
              <CardTitle asChild className="font-sans">
                <h2>{t('settings.accessTitle')}</h2>
              </CardTitle>
              <CardDescription>{t('settings.accessHelp')}</CardDescription>
            </div>
            {/* a manager invites a plain member onto this workspace only (Q31); archived: nothing to join */}
            {archived ? null : (
              <Button onClick={() => setInviting(true)}>{t('member:invite.action')}</Button>
            )}
          </CardHeader>
          <CardContent className="grid gap-4">
            {members.isPending || grants.isPending ? (
              <section
                aria-busy="true"
                aria-label={t('settings.accessLoading')}
                className="grid gap-3"
              >
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </section>
            ) : null}
            {members.isError || grants.isError ? (
              <ErrorState
                size="compact"
                title={t('settings.accessError')}
                description={(members.error ?? grants.error)?.message}
                onRetry={() => {
                  void members.refetch();
                  void grants.refetch();
                }}
              />
            ) : null}
            {members.data && grants.data
              ? grantable.map((member) => (
                  <section
                    key={member.user_id}
                    aria-label={memberDisplayName(member)}
                    className="grid gap-2"
                  >
                    <h3 className="font-bold text-heading">
                      {memberDisplayName(member)}{' '}
                      <span className="text-sm font-normal text-muted-foreground">
                        {member.email}
                      </span>
                    </h3>
                    <GrantsEditor
                      org={org}
                      member={member}
                      grants={grants.data}
                      targets={[{ id: workspace.id, name: workspace.name, archived }]}
                    />
                  </section>
                ))
              : null}
            <PendingInvitations org={org} workspaces={[workspace]} />
          </CardContent>
          <InviteMemberDialog
            open={inviting}
            onOpenChange={setInviting}
            org={org}
            scope={{ kind: 'workspace', workspace }}
            workspaces={[workspace]}
          />
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('settings.archiveTitle')}</h2>
          </CardTitle>
          <CardDescription>{t('settings.archiveHelp')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ArchiveWorkspaceButton workspace={workspace} />
        </CardContent>
      </Card>
    </PageLayout>
  );
}
